import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import WhatsAppWidget from "./WhatsAppWidget";
import CookieBanner from "./CookieBanner";
import { ArrowRight, Phone, Wifi, Share2, Smartphone, CheckCircle, Star } from "lucide-react";
import { Link } from "react-router-dom";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    return !localStorage.getItem('cookiesAccepted');
  });

  // Scroll to top when component mounts or route changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, location]);

  const services = {
    "office-smart": {
      title: "Office Smart",
      subtitle: "Centralino Virtuale Professionale",
      price: "29,90",
      originalPrice: "39,90",
      icon: Phone,
      backgroundImage: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/qo8weivx_office%20smart.jpg",
      description: "La soluzione completa per la gestione delle comunicazioni aziendali. Un centralino virtuale che riduce i costi e semplifica la gestione delle chiamate.",
      features: [
        "Centralino virtuale completo",
        "Riduzione costi fino al 70%",
        "Gestione semplificata",
        "Scalabilità automatica",
        "Supporto H24/7",
        "App mobile inclusa",
        "IVR personalizzabile",
        "Registrazione chiamate"
      ],
      benefits: [
        "Niente hardware da acquistare",
        "Configurazione in 24h",
        "Assistenza dedicata",
        "Backup automatico"
      ],
      projects: [
        {
          title: "Studio Legale P.L.",
          description: "Gestione di 50+ chiamate giornaliere con IVR personalizzato per smistamento automatico clienti.",
          results: "Riduzione del 60% dei tempi di attesa e miglioramento della customer satisfaction."
        },
        {
          title: "Infissi Sarni Spa",
          description: "Centralino per 3 sedi con 100 interni, integrazione CRM e registrazione chiamate.",
          results: "Taglio dei costi telefonici del 70% e migliore coordinamento tra sedi."
        }
      ],
      testimonial: {
        name: "Fabio Parenti",
        company: "CEO, TechStart Srl",
        text: "Office Smart ha rivoluzionato la nostra comunicazione aziendale. Setup velocissimo e supporto eccellente. Lo consiglio vivamente!",
        rating: 5
      }
    },
    "office-plus": {
      title: "Office Plus e Maxi",
      subtitle: "Connettività Fibra Ultra-Veloce",
      price: "39,90",
      originalPrice: "49,90",
      icon: Wifi,
      backgroundImage: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/e2n8yeik_office%20maxi.jpg",
      description: "Fibra ottica dedicata fino a 1 Gigabit per aziende che necessitano di velocità elevate per videoconferenze, cloud e trasferimento dati.",
      features: [
        "Fibra ottica fino a 1 Gbps",
        "Upload simmetrico",
        "Videoconferenze HD/4K",
        "Priorità traffico business",
        "SLA garantito 99.9%",
        "IP statico incluso",
        "Supporto tecnico H24",
        "Backup automatico 4G"
      ],
      benefits: [
        "Installazione gratuita",
        "Nessun vincolo contrattuale",
        "Modem WiFi 6 incluso",
        "Monitoraggio proattivo"
      ],
      projects: [
        {
          title: "Agenzia Creativa Digital",
          description: "Connessione 1Gb per team di 30 persone, transfer video 4K e backup cloud in tempo reale.",
          results: "Riduzione del 90% dei tempi di upload e produttività aumentata del 40%."
        },
        {
          title: "Clinica Medica Salute+",
          description: "Fibra dedicata per telemedicina, cartelle cliniche digitali e videoconferenze mediche.",
          results: "Zero interruzioni di servizio e consultazioni remote fluide al 100%."
        }
      ],
      testimonial: {
        name: "Laura Bianchi",
        company: "CTO, InnovaTech",
        text: "La fibra Office Plus ha trasformato il nostro workflow. Velocità incredibile e stabilità assoluta per tutti i nostri processi digitali.",
        rating: 5
      }
    },
    "super-unlimited": {
      title: "Super Unlimited",
      subtitle: "Piano Mobile Senza Limiti",
      price: "14,99",
      originalPrice: "24,99",
      icon: Smartphone,
      backgroundImage: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/098aqrgp_super.jpg",
      description: "Il piano mobile più completo per professionisti e aziende. Dati, chiamate e SMS illimitati con rete 5G per massima produttività ovunque.",
      features: [
        "Dati illimitati in Italia",
        "Chiamate e SMS illimitati",
        "Rete 5G ultra-veloce",
        "Roaming EU 50GB inclusi",
        "Hotspot senza limiti",
        "Priorità di rete",
        "App business incluse",
        "Supporto prioritario"
      ],
      benefits: [
        "Attivazione immediata",
        "SIM consegna gratuita",
        "Portabilità veloce",
        "Fatturazione elettronica"
      ],
      projects: [
        {
          title: "Sales Team Nazionale",
          description: "15 commerciali con Super Unlimited per videocall, CRM mobile e condivisione dati illimitata.",
          results: "Aumento delle vendite del 35% grazie alla connettività sempre attiva."
        },
        {
          title: "Startup TechMobile",
          description: "Piano aziendale per 25 dipendenti, lavoro da remoto e smart working completo.",
          results: "Riduzione costi mobile del 50% con prestazioni superiori del 200%."
        }
      ],
      testimonial: {
        name: "Alessandro Verde",
        company: "Sales Director, FastSell",
        text: "Super Unlimited è perfetto per il nostro team commerciale. Connessione veloce ovunque e nessuna preoccupazione sui consumi dati.",
        rating: 5
      }
    },
    "machine2machine": {
      title: "Machine2Machine",
      subtitle: "Soluzioni IoT e Monitoraggio",
      price: "1,00",
      originalPrice: "5,00",
      icon: Share2,
      backgroundImage: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/bzoqzgaw_machine.jpg",
      description: "Tecnologie avanzate per il controllo remoto, videosorveglianza e sistemi di allarme. Monitora e gestisci i tuoi impianti 24/7.",
      features: [
        "SIM M2M dedicate",
        "Monitoraggio H24/365",
        "Controllo remoto dispositivi",
        "Videosorveglianza IP",
        "Allarmi automatici",
        "Dashboard web completa",
        "API personalizzabili",
        "Backup ridondante"
      ],
      benefits: [
        "Installazione chiavi in mano",
        "Configurazione personalizzata",
        "Manutenzione inclusa",
        "Scalabilità illimitata"
      ],
      projects: [
        {
          title: "Catena Retail MultiStore",
          description: "Sistema M2M per 50 punti vendita: videosorveglianza, controllo accessi e monitoraggio impianti.",
          results: "Riduzione furti del 80% e gestione centralizzata di tutti i negozi."
        },
        {
          title: "Impianto Industriale Green",
          description: "Monitoraggio IoT per pannelli solari, controllo temperatura e sistemi di sicurezza.",
          results: "Ottimizzazione energetica del 45% e zero interruzioni non pianificate."
        }
      ],
      testimonial: {
        name: "Giovanni Neri",
        company: "Facility Manager, SmartBuilding",
        text: "Le soluzioni M2M di Lovecchio Group hanno rivoluzionato la gestione dei nostri impianti. Controllo totale e massima affidabilità.",
        rating: 5
      }
    }
  };

  const service = services[slug];

  if (!service) {
    return <div>Servizio non trovato</div>;
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-black text-white py-32 overflow-hidden min-h-screen flex items-center"
        style={{
          backgroundImage: service.backgroundImage ? `url(${service.backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Creative Overlay */}
        {service.backgroundImage && (
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/80 to-transparent"></div>
        )}
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-orange-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-40 left-10 w-24 h-24 bg-orange-600 rounded-full opacity-30 blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Creative Text Layout */}
            <div className="space-y-8">
              {/* Icon with glow effect */}
              <div className="relative">
                <service.icon size={120} className="text-orange-400 drop-shadow-2xl filter drop-shadow-[0_0_30px_rgba(251,146,60,0.5)]" />
              </div>
              
              {/* Creative Title Layout */}
              <div className="space-y-4">
                <h1 className="text-7xl font-black leading-tight">
                  <span className="bg-gradient-to-r from-white via-gray-200 to-orange-200 bg-clip-text text-transparent">
                    {service.title}
                  </span>
                </h1>
                
                {/* Subtitle with less offset */}
                <p className="text-2xl font-light bg-gradient-to-l from-gray-300 to-white bg-clip-text text-transparent italic transform translate-x-4">
                  {service.subtitle}
                </p>
              </div>
              
              {/* Description with creative positioning */}
              <div className="relative">
                <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-orange-400 to-red-600 rounded-full"></div>
                <p className="text-xl text-gray-200 leading-relaxed pl-8 font-light">
                  {service.description}
                </p>
              </div>
            </div>
            
            {/* Right Side - Price Display with Creative Layout */}
            <div className="flex justify-center lg:justify-end">
              {/* Price Container with glow - Smaller and Centered */}
              <div className="relative max-w-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-black/80 backdrop-blur-sm border border-orange-500/30 rounded-3xl p-6 text-center">
                  <p className="text-xs text-gray-400 mb-2 uppercase tracking-widest">a partire da</p>
                  <div className="flex items-baseline justify-center mb-3">
                    <span className="text-6xl font-black bg-gradient-to-br from-orange-400 via-orange-500 to-red-500 bg-clip-text text-transparent drop-shadow-2xl">
                      {service.price}
                    </span>
                    <span className="text-2xl text-gray-300 ml-2 font-light">€/mese</span>
                  </div>
                  {service.originalPrice && (
                    <p className="text-md text-gray-500 line-through mb-4">era {service.originalPrice}€/mese</p>
                  )}
                  
                  {/* CTA Button with glow effect */}
                  <Link 
                    to="/preventivo"
                    className="inline-block bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-black font-bold px-6 py-3 rounded-full text-md transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_30px_rgba(251,146,60,0.6)] uppercase tracking-wider"
                  >
                    Richiedi Preventivo Gratuito
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent"></div>
      </section>

      {/* Features Section - Smaller */}
      <section className="bg-gray-900 py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Features List - Compact */}
            <div className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-colors duration-300">
              <div className="flex items-center mb-6">
                <div className="bg-orange-500 rounded-full p-2 mr-3">
                  <CheckCircle size={24} className="text-black" />
                </div>
                <h2 className="text-2xl font-bold text-white">Caratteristiche Principali</h2>
              </div>
              <div className="grid gap-3">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-gray-200 p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors duration-200">
                    <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 flex-shrink-0"></div>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Benefits - Compact */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-black">
              <div className="flex items-center mb-6">
                <div className="bg-black rounded-full p-2 mr-3">
                  <Star size={24} className="text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold">Vantaggi Esclusivi</h2>
              </div>
              <div className="grid gap-3">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center p-3 bg-black bg-opacity-20 rounded-lg backdrop-blur-sm">
                    <div className="w-2 h-2 bg-black rounded-full mr-3 flex-shrink-0"></div>
                    <span className="text-sm font-semibold">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section - Centered */}
      <section className="bg-black py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Progetti di Successo</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {service.projects.map((project, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-8 text-center">
                <h3 className="text-2xl font-bold text-orange-400 mb-4">{project.title}</h3>
                <p className="text-gray-300 mb-6 text-center">{project.description}</p>
                <div className="border-t-4 border-orange-400 pt-4">
                  <p className="text-white font-semibold text-lg mb-2">RISULTATI:</p>
                  <p className="text-gray-300">{project.results}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-12">Cosa Dicono i Nostri Clienti</h2>
            <div className="bg-gray-800 rounded-lg p-8">
              <div className="flex justify-center mb-4">
                {[...Array(service.testimonial.rating)].map((_, i) => (
                  <Star key={i} size={24} className="text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-xl text-gray-300 italic mb-6">
                "{service.testimonial.text}"
              </blockquote>
              <div>
                <p className="text-white font-semibold">{service.testimonial.name}</p>
                <p className="text-orange-400">{service.testimonial.company}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-orange-500 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-black mb-6">
            Pronto a iniziare con {service.title}?
          </h2>
          <p className="text-xl text-black mb-8">
            Richiedi un preventivo gratuito e scopri come possiamo aiutare la tua azienda
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link 
              to="/preventivo"
              className="bg-black text-white font-bold px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-200"
            >
              Richiedi Preventivo
            </Link>
            <Link 
              to="/trova-agente"
              className="bg-white text-black font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              Parla con un Esperto
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
      {showCookieBanner && (
        <CookieBanner onClose={() => setShowCookieBanner(false)} />
      )}
    </div>
  );
};

export default ServiceDetailPage;